import { Drawer } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { AppliedAvailabilityFilters } from './filters.types';
import { FilterFlexpools } from '../../filters/inputs/flexpools/filter-flexpools';
import { FilterWrapper } from '../../filters/filter-wrapper';
import { CompanyFlexpools } from '../../../../api/services/availability/availability-service.schema';
import { LwAutocomplete } from '../../../../redesign';
import { FlexpoolsFilterFormContext } from '../../filters/inputs/flexpools/filter-flexpools.types';
import { useSiteDetection } from '../../../hooks';

const ALL_FLEXPOOLS_VALUE = 'all';

export const AvailabilityFilters = ({
  companyFlexPools,
  companyInput,
  isOpen,
  companies,
  company,
  toggleFilters,
  handleCancel,
  handleFiltersChange,
  setCompany,
  setCompanyInput,
  currentFilters,
  allFlexpoolsSelected,
  isFlexPoolDataFilled,
}: {
  companyFlexPools: CompanyFlexpools;
  companies: { id: string; name: string }[];
  isOpen: boolean;
  company?: { value: string; label: string };
  toggleFilters: () => void;
  handleCancel: () => void;
  setCompany: ({ value, label }?: { value: string; label: string }) => void;
  setCompanyInput: (value: string) => void;
  handleFiltersChange: (filters: AppliedAvailabilityFilters) => void;
  currentFilters: string[];
  companyInput: string;
  allFlexpoolsSelected: boolean;
  isFlexPoolDataFilled: boolean;
}) => {
  const { t } = useTranslation('web-common');
  const allFlexpoolsOption = useMemo(
    () => ({
      value: ALL_FLEXPOOLS_VALUE,
      label: t('availability-page.filters.flexpools.all.label', { defaultValue: 'Alle flexpools' }),
    }),
    [t]
  );
  const form = useForm<FlexpoolsFilterFormContext>({
    defaultValues: {
      flexpools: allFlexpoolsSelected
        ? [allFlexpoolsOption]
        : currentFilters.flatMap((id) => {
            const matchingFlexpoolFromQuery = companyFlexPools.flexPools.find(
              (flexPool) => flexPool.id === id
            );
            return matchingFlexpoolFromQuery
              ? [{ value: id, label: matchingFlexpoolFromQuery.name }]
              : [];
          }),
    },
  });
  const { site } = useSiteDetection();
  const isOgp = site === 'ogp';
  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.name,
      })),
    [companies]
  );

  const flexPoolOptions = useMemo(() => {
    const shouldIncludeAllFlexpoolsOption = !isOgp && !!company?.value;
    const companyFlexPoolOptions = companyFlexPools.flexPools.map((flexPool) => ({
      value: flexPool.id,
      label: flexPool.name,
    }));

    return shouldIncludeAllFlexpoolsOption
      ? [allFlexpoolsOption, ...companyFlexPoolOptions]
      : companyFlexPoolOptions;
  }, [allFlexpoolsOption, company?.value, companyFlexPools.flexPools, isOgp]);
  const isSubmitDisabled = !isOgp && !!company?.value && !isFlexPoolDataFilled;

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      form.handleSubmit((data) => {
        const selectedAll = data.flexpools.some(
          (flexPool) => flexPool.value === ALL_FLEXPOOLS_VALUE
        );
        handleFiltersChange({
          flexPoolIds: selectedAll
            ? companyFlexPools.flexPools.map(({ id }) => id)
            : data.flexpools.map((flexPool) => flexPool.value),
          allFlexpoolsSelected: selectedAll,
          company,
        });
        toggleFilters();
      })();
    },
    [company, companyFlexPools, form, handleFiltersChange, toggleFilters]
  );
  const handleClear = useCallback(() => {
    form.reset({
      flexpools: [],
    });
    handleFiltersChange({ flexPoolIds: [], allFlexpoolsSelected: false });
    setCompany(undefined);
  }, [form, handleFiltersChange, setCompany]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={handleCancel}
          onClear={handleClear}
          onSubmit={handleSubmit}
          isSubmitDisabled={isSubmitDisabled}
        >
          {!isOgp && (
            <LwAutocomplete
              name="company"
              isLoading={false}
              value={company ?? null}
              label={t('company-filter.autocomplete.label')}
              getOptionLabel={(value) => value?.label ?? ''}
              isOptionEqualToValue={(option, value) => {
                return option?.value === value?.value;
              }}
              noOptionsText={t('company-filter.autocomplete.min-chars', { count: 3 })}
              inputValue={companyInput ?? ''}
              onInputChange={(_event, value) => {
                setCompanyInput(value);
              }}
              getOptionKey={(option) => option.value}
              options={companyOptions}
              onChange={(_event, option) => {
                form.setValue('flexpools', option ? [allFlexpoolsOption] : []);
                if (option?.value && option.label) {
                  setCompany({ value: option.value, label: option.label });
                } else {
                  setCompany(undefined);
                }
              }}
              error={false}
              helperText={undefined}
            />
          )}
          <FilterFlexpools
            companyFlexpools={flexPoolOptions}
            disabled={!isOgp && !company?.value}
            onChange={(_event, values, reason, details) => {
              if (reason === 'selectOption' && details?.option.value === ALL_FLEXPOOLS_VALUE) {
                form.setValue('flexpools', [allFlexpoolsOption]);
              } else if (
                reason === 'selectOption' &&
                values.some(({ value }) => value === ALL_FLEXPOOLS_VALUE)
              ) {
                form.setValue(
                  'flexpools',
                  values.filter(({ value }) => value !== ALL_FLEXPOOLS_VALUE)
                );
              }
            }}
          />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
